import { Feature } from "ol";
import * as React from "react";
import { createContext, Dispatch, FC, SetStateAction, useContext, useEffect, useState } from "react";
import { SheetSelectMode } from "../Map/enums";
import { useMapContext } from "./MapContext";

export interface IMapDownloadContext {
    sheetSelectMode: SheetSelectMode;
    setSheetSelectMode: Dispatch<SetStateAction<SheetSelectMode>>;
    identifiedSheet: Feature[];
    setIdentifiedSheet: Dispatch<SetStateAction<Feature[]>>;
    sheetFeatures: Feature[];
    setSheetFeatures: Dispatch<SetStateAction<Feature[]>>;
    sheetSelectPolygon: Feature;
    setSheetSelectPolygon: Dispatch<SetStateAction<Feature>>;
    limitExceeded: boolean;
    setLimitExceeded: Dispatch<SetStateAction<boolean>>;
}

const MapDownloadContext = createContext<IMapDownloadContext>(null);

export const useMapDownloadContext = (): IMapDownloadContext => useContext(MapDownloadContext);

export const MapDownloadContextProvider: FC = (props: React.PropsWithChildren<IMapDownloadContext>) => {
    const { configA } = useMapContext();

    const [sheetSelectMode, setSheetSelectMode] = useState<SheetSelectMode>();
    const [identifiedSheet, setIdentifiedSheet] = useState<Feature[]>();
    const [sheetFeatures, setSheetFeatures] = useState<Feature[]>([]);
    const [sheetSelectPolygon, setSheetSelectPolygon] = useState<Feature>();
    const [limitExceeded, setLimitExceeded] = useState<boolean>(false);

    useEffect(() => {
        if (!identifiedSheet || !configA) {
            return;
        }

        const ids = new Set(sheetFeatures.map((f) => f.get("OBJECTID").toString()));
        const identifiedSheets = identifiedSheet.filter((f) => !ids.has(f.get("OBJECTID").toString()));

        const combined = [...sheetFeatures, ...identifiedSheets];

        combined.length >= configA.geotelpa.mapDownloadMaxCount
            ? setSheetFeatures(combined.filter((f, i) => (i < configA.geotelpa.mapDownloadMaxCount ? f : null)))
            : setSheetFeatures(combined);
    }, [identifiedSheet]);

    useEffect(() => {
        configA?.geotelpa?.mapDownloadMaxCount &&
            setLimitExceeded(sheetFeatures && sheetFeatures.length >= configA.geotelpa.mapDownloadMaxCount);
    }, [sheetFeatures]);

    const values = {
        sheetSelectMode,
        setSheetSelectMode,
        identifiedSheet,
        setIdentifiedSheet,
        sheetFeatures,
        setSheetFeatures,
        sheetSelectPolygon,
        setSheetSelectPolygon,
        limitExceeded,
        setLimitExceeded
    };

    return <MapDownloadContext.Provider value={values}>{props.children}</MapDownloadContext.Provider>;
};
